/**
 * EpostOppfolgingsforslag - Modal som viser AI-foreslåtte oppfølginger
 *
 * Viser alle forslag for en e-post med mulighet til å:
 * - Godkjenne alle
 * - Godkjenne enkeltvis
 * - Justere og godkjenne
 * - Avvise
 */

import React, { useState, useEffect } from 'react';
import ForslagCard from './ForslagCard';
import crmApi from '../api/crm';
import './EpostOppfolgingsforslag.css';

export default function EpostOppfolgingsforslag({ epost, onLukk, onOppgaverOpprettet }) {
  const [forslag, setForslag] = useState([]);
  const [laster, setLaster] = useState(true);
  const [feilmelding, setFeilmelding] = useState(null);
  const [godkjennerAlle, setGodkjennerAlle] = useState(false);

  useEffect(() => {
    lastForslag();
  }, [epost.id]);

  const lastForslag = async () => {
    try {
      setLaster(true);
      setFeilmelding(null);

      const response = await crmApi.getOppfolgingsforslag(epost.id);

      if (response.forslag && response.forslag.length > 0) {
        setForslag(response.forslag);
      } else {
        setFeilmelding('AI fant ingen oppfølgingsbehov for denne e-posten');
      }
    } catch (error) {
      console.error('Feil ved lasting av forslag:', error);
      setFeilmelding('Kunne ikke laste forslag: ' + error.message);
    } finally {
      setLaster(false);
    }
  };

  const handleGodkjennForslag = async (forslagId, justeringer = {}) => {
    try {
      await crmApi.godkjennForslag(forslagId, justeringer);

      // Fjern forslag fra listen
      setForslag(prev => prev.filter(f => f.id !== forslagId));

      // Hvis ingen forslag igjen, lukk modal
      if (forslag.length === 1) {
        if (onOppgaverOpprettet) onOppgaverOpprettet();
        onLukk();
      }
    } catch (error) {
      console.error('Feil ved godkjenning:', error);
      throw error;
    }
  };

  const handleAvvisForslag = async (forslagId) => {
    try {
      await crmApi.avvisForslag(forslagId);

      // Fjern forslag fra listen
      setForslag(prev => prev.filter(f => f.id !== forslagId));

      // Hvis ingen forslag igjen, lukk modal
      if (forslag.length === 1) {
        onLukk();
      }
    } catch (error) {
      console.error('Feil ved avvisning:', error);
      throw error;
    }
  };

  const handleJusterForslag = async (forslagId, endringer) => {
    try {
      const oppdatertForslag = await crmApi.justerForslag(forslagId, endringer);

      // Oppdater forslag i listen
      setForslag(prev =>
        prev.map(f => f.id === forslagId ? { ...f, ...oppdatertForslag.forslag } : f)
      );
    } catch (error) {
      console.error('Feil ved justering:', error);
      throw error;
    }
  };

  const handleGodkjennAlle = async () => {
    if (!window.confirm(`Godkjenn alle ${forslag.length} forslag og opprett oppgaver?`)) {
      return;
    }

    try {
      setGodkjennerAlle(true);
      await crmApi.godkjennAlleForslag(epost.id);

      // Tøm forslag
      setForslag([]);

      // Callback og lukk
      if (onOppgaverOpprettet) onOppgaverOpprettet();
      onLukk();
    } catch (error) {
      console.error('Feil ved godkjenning av alle:', error);
      alert('Kunne ikke godkjenne alle forslag: ' + error.message);
    } finally {
      setGodkjennerAlle(false);
    }
  };

  return (
    <div className="oppfolgingsforslag-overlay" onClick={onLukk}>
      <div className="oppfolgingsforslag-modal" onClick={(e) => e.stopPropagation()}>
        <div className="oppfolgingsforslag-header">
          <div>
            <h2>🤖 AI-foreslåtte oppfølginger</h2>
            <p className="oppfolgingsforslag-epost-info">
              <strong>E-post:</strong> {epost.subject}
              <br />
              <strong>Fra:</strong> {epost.from_address || epost.from}
            </p>
          </div>
          <button className="oppfolgingsforslag-lukk" onClick={onLukk}>
            ✕
          </button>
        </div>

        <div className="oppfolgingsforslag-body">
          {laster ? (
            <div className="oppfolgingsforslag-laster">
              <div className="spinner"></div>
              <p>AI analyserer e-postinnhold...</p>
            </div>
          ) : feilmelding ? (
            <div className="oppfolgingsforslag-feil">
              <p>{feilmelding}</p>
              <button className="btn-forslag-lukk" onClick={onLukk}>
                Lukk
              </button>
            </div>
          ) : forslag.length === 0 ? (
            <div className="oppfolgingsforslag-tom">
              <p>✅ Alle forslag er behandlet</p>
              <button className="btn-forslag-lukk" onClick={onLukk}>
                Lukk
              </button>
            </div>
          ) : (
            <>
              <div className="oppfolgingsforslag-info">
                <p>
                  AI har funnet <strong>{forslag.length}</strong> oppfølgingsbehov i denne e-posten.
                  Gjennomgå og godkjenn forslagene nedenfor.
                </p>
              </div>

              <div className="oppfolgingsforslag-liste">
                {forslag.map((f) => (
                  <ForslagCard
                    key={f.id}
                    forslag={f}
                    onGodkjenn={(justeringer) => handleGodkjennForslag(f.id, justeringer)}
                    onAvvis={() => handleAvvisForslag(f.id)}
                    onJuster={(endringer) => handleJusterForslag(f.id, endringer)}
                  />
                ))}
              </div>

              <div className="oppfolgingsforslag-bulk-actions">
                <button
                  className="btn-godkjenn-alle"
                  onClick={handleGodkjennAlle}
                  disabled={godkjennerAlle}
                >
                  {godkjennerAlle ? '⏳ Godkjenner...' : `✅ Godkjenn alle ${forslag.length} forslag`}
                </button>
                <button className="btn-avbryt" onClick={onLukk}>
                  Avbryt
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
